import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { PermissionNames } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { useSeasonPoolService } from "~pages/SeasonPools/hooks/useSeasonPoolService.ts";
import { AddSeasonPoolModal } from "~pages/SeasonPools/SeasonPoolListPage/components/AddSeasonPoolModal.tsx";
import { defaultSeasonPoolExportFieldNames, seasonPoolExportFields } from "~pages/SeasonPools/SeasonPoolListPage/constants/exportFields.ts";
import { useFetchSeasonPools } from "~pages/SeasonPools/SeasonPoolListPage/hooks/useFetchSeasonPools.ts";
import { useGetSeasonPoolTableColumns } from "~pages/SeasonPools/SeasonPoolListPage/hooks/useGetSeasonPoolTableColumns.tsx";

const DEFAULT_SORTER = { columnKey: "poolOrder", order: "ascend" as const };

const SeasonPoolListPage = () => {
  const { sport, customerId, organizationId, seasonId } = useParams();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const paginationConfig = useHandlePagination(20);
  const [searchText, setSearchText] = useState("");
  const [massEditMode, setMassEditMode] = useState(false);
  const [isAddSeasonPoolModalOpen, setIsAddSeasonPoolModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const { uiConfiguration } = useSportConfiguration();
  const dataTableUiConfiguration = uiConfiguration.pages.seasonPools.listPage;
  const dataTableImportConfiguration = uiConfiguration.pages.seasonPools.listPage.importConfig;
  const columns = useGetSeasonPoolTableColumns(massEditMode);
  const { deleteSeasonPool, isDeletePending, importSeasonPools, exportSeasonPools, isExportPending } = useSeasonPoolService();

  const handleSearch = useCallback(
    (value: string) => {
      paginationConfig.resetPagination();
      setSearchText(value);
    },
    [paginationConfig],
  );

  const listParams = useMemo(
    () => ({
      limit: paginationConfig.pageSize.toString(),
      offset: paginationConfig.offset.toString(),
      sortBy: sortBy || "",
      nameLocalContains: searchText,
    }),
    [paginationConfig.offset, paginationConfig.pageSize, searchText, sortBy],
  );

  const { data, refetch, isPending } = useFetchSeasonPools({
    customParams: listParams,
    enabled: sortBy !== null,
  });

  const getNavigateUrl = (poolCode: string) => `/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/pools/${poolCode}`;

  const onMassEditModeChangedHandler = useCallback((checked: boolean) => {
    setMassEditMode(checked);
  }, []);

  return (
    <>
      <AddSeasonPoolModal isOpen={isAddSeasonPoolModalOpen} setIsOpen={setIsAddSeasonPoolModalOpen} />
      <DataTable
        handleRefresh={async () => {
          await refetch();
        }}
        title={formatMessage({ id: "pools" })}
        columns={columns}
        rowActions={{
          base: {
            manage: {
              onClick: (row) => navigate(getNavigateUrl(row.poolCode)),
            },
            delete: {
              onClick: (row) => {
                void deleteSeasonPool(row.poolCode);
              },
              itemName: (row) => row.nameLocal || row.poolCode,
              loading: isDeletePending,
            },
          },
        }}
        rowKey="poolCode"
        rowNameKey="nameLocal"
        dataSource={data?.data || []}
        columnChooserLocalStorageKey={`${sport}.tables.settings.columns.seasonPools`}
        isDataLoading={isPending}
        handleAdd={() => setIsAddSeasonPoolModalOpen(true)}
        addButtonIntlKey="pools.add"
        searchConfig={{
          handleSearch,
          searchText,
        }}
        onMassEditModeChangedHandler={onMassEditModeChangedHandler}
        massEditMode={massEditMode}
        columnSortConfig={{
          sorterLocalStorageKey: `${sport}.tables.settings.sort.seasonPools`,
          defaultSorter: DEFAULT_SORTER,
          onSortChange: setSortBy,
        }}
        exportConfig={{
          handleExport: exportSeasonPools,
          loading: isExportPending,
          defaultFields: defaultSeasonPoolExportFieldNames,
          groupedFields: seasonPoolExportFields,
          localStorageKey: "export.seasonPools",
        }}
        importConfig={{
          ...dataTableImportConfiguration,
          handleImport: importSeasonPools,
        }}
        permissionNames={[PermissionNames.POOL]}
        dataTableUiConfiguration={dataTableUiConfiguration}
        paginationConfig={{ ...paginationConfig, hasNext: data?.hasNextLink }}
      />
    </>
  );
};

export default SeasonPoolListPage;
